import { glob } from 'glob';
import { join, relative, extname } from 'path';
import { Route } from '../../types';

export class AutoRouter {
    private appDir: string;
    private routes: Route[] = [];

    constructor(appDir: string) {
        this.appDir = appDir;
    }

    async scanRoutes(): Promise<Route[]> {
        const pattern = join(this.appDir, '**/*.{tsx,jsx,ts,js}');
        const files = await glob(pattern, { ignore: ['**/node_modules/**'] });

        this.routes = files
            .map(file => this.fileToRoute(file))
            .filter(route => route !== null) as Route[];

        return this.routes;
    }

    private fileToRoute(filePath: string): Route | null {
        const relativePath = relative(this.appDir, filePath);
        const ext = extname(filePath);

        // Ignorar archivos que no son páginas
        if (ext !== '.tsx' && ext !== '.jsx' && ext !== '.ts' && ext !== '.js') {
            return null;
        }

        // Ignorar archivos que empiezan con _
        if (relativePath.includes('/_') || relativePath.startsWith('_')) {
            return null;
        }

        let path = relativePath
            .replace(ext, '')
            .replace(/\\/g, '/');

        // Manejar index files
        if (path.endsWith('/index')) {
            path = path.replace('/index', '');
        }

        // Convertir a ruta de URL
        if (path === 'index') {
            path = '/';
        } else {
            path = '/' + path;
        }

        // Detectar rutas dinámicas
        const isDynamic = path.includes('[') && path.includes(']');
        const params = this.extractParams(path);

        return {
            path,
            component: filePath,
            isDynamic,
            params,
            title: this.generateTitle(path)
        };
    }

    private extractParams(path: string): string[] {
        const matches = path.match(/\[([^\]]+)\]/g);
        return matches ? matches.map(match => match.slice(1, -1)) : [];
    }

    private generateTitle(path: string): string {
        if (path === '/') return 'Home';

        return path
            .split('/')
            .filter(Boolean)
            .map(segment => {
                if (segment.startsWith('[') && segment.endsWith(']')) {
                    return segment.slice(1, -1);
                }
                return segment.charAt(0).toUpperCase() + segment.slice(1);
            })
            .join(' ');
    }

    findRouteByPath(path: string): Route | null {
        // Buscar ruta exacta primero
        let route = this.routes.find(r => r.path === path);
        if (route) return route;

        // Buscar rutas dinámicas
        for (const r of this.routes) {
            if (r.isDynamic && this.matchesDynamicRoute(r.path, path)) {
                return r;
            }
        }

        return null;
    }

    private matchesDynamicRoute(routePath: string, actualPath: string): boolean {
        const routeSegments = routePath.split('/');
        const actualSegments = actualPath.split('/');

        if (routeSegments.length !== actualSegments.length) {
            return false;
        }

        for (let i = 0; i < routeSegments.length; i++) {
            const routeSegment = routeSegments[i];
            const actualSegment = actualSegments[i];

            if (routeSegment.startsWith('[') && routeSegment.endsWith(']')) {
                // Es un parámetro dinámico
                continue;
            }

            if (routeSegment !== actualSegment) {
                return false;
            }
        }

        return true;
    }

    extractParamsFromPath(route: Route, actualPath: string): { [key: string]: string } {
        const params: { [key: string]: string } = {};

        if (!route.isDynamic) return params;

        const routeSegments = route.path.split('/');
        const actualSegments = actualPath.split('/');

        for (let i = 0; i < routeSegments.length; i++) {
            const routeSegment = routeSegments[i];

            if (routeSegment.startsWith('[') && routeSegment.endsWith(']')) {
                const paramName = routeSegment.slice(1, -1);
                params[paramName] = actualSegments[i];
            }
        }

        return params;
    }

    getRoutes(): Route[] {
        return this.routes;
    }
}
